import { z } from "zod";
import { AssetSchema } from "./assets";

export const ORGANIZATION_FILE_ENTRY_TYPE = {
  FILE: "FILE",
  FOLDER: "FOLDER",
} as const;

export const ORGANIZATION_FILE_ENTRY_TYPE_VALUES = [
  ORGANIZATION_FILE_ENTRY_TYPE.FILE,
  ORGANIZATION_FILE_ENTRY_TYPE.FOLDER,
] as const;

export const OrganizationFileEntryTypeSchema = z.enum(
  ORGANIZATION_FILE_ENTRY_TYPE_VALUES,
);
export type OrganizationFileEntryType = z.infer<
  typeof OrganizationFileEntryTypeSchema
>;

export const OrganizationFileSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  assetId: z.string().nullable(),
  type: OrganizationFileEntryTypeSchema,
  parentFolderId: z.string().nullable(),
  name: z.string(),
  path: z.string(),
  depth: z.number(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type OrganizationFile = z.infer<typeof OrganizationFileSchema>;

export const HydratedOrganizationFileSchema = OrganizationFileSchema.extend({
  asset: AssetSchema.nullable(),
});
export type HydratedOrganizationFile = z.infer<
  typeof HydratedOrganizationFileSchema
>;

export const OrganizationFileBreadcrumbSchema = z.object({
  id: z.string(),
  name: z.string(),
});
export type OrganizationFileBreadcrumb = z.infer<
  typeof OrganizationFileBreadcrumbSchema
>;

export const AddOrganizationFileImportSchema = z.object({
  assetId: z.string(),
  relativePath: z.string().min(1).optional(),
});
export type AddOrganizationFileImport = z.infer<
  typeof AddOrganizationFileImportSchema
>;

export const AddOrganizationFilesParamsSchema = z
  .object({
    parentFolderId: z.string().nullable().optional(),
    assetIds: z
      .array(z.string())
      .min(1, "At least one asset ID is required")
      .optional(),
    files: z
      .array(AddOrganizationFileImportSchema)
      .min(1, "At least one file is required")
      .optional(),
    folderPaths: z
      .array(z.string().min(1))
      .min(1, "At least one folder path is required")
      .optional(),
    skipExistingPaths: z.boolean().optional(),
  })
  .refine(
    (data) =>
      data.assetIds !== undefined ||
      data.files !== undefined ||
      data.folderPaths !== undefined,
    {
      message: "Either assetIds, files, or folderPaths is required",
    },
  );
export type AddOrganizationFilesParams = z.infer<
  typeof AddOrganizationFilesParamsSchema
>;

/** Caps entries per import-key search request; clients batch larger picks. */
export const MAX_IMPORT_KEY_SEARCH_ENTRIES = 1_000;

export const SearchOrganizationFileImportKeysParamsSchema = z.object({
  parentFolderId: z.string().nullable().optional(),
  entries: z
    .array(
      z.object({
        relativePath: z.string().min(1),
        fileSize: z.number().nullable(),
        fileType: z.string().nullable(),
      }),
    )
    .min(1)
    .max(MAX_IMPORT_KEY_SEARCH_ENTRIES),
});
export type SearchOrganizationFileImportKeysParams = z.infer<
  typeof SearchOrganizationFileImportKeysParamsSchema
>;

export const SearchOrganizationFileImportKeysResponseSchema = z.object({
  existingKeys: z.array(z.string()),
});
export type SearchOrganizationFileImportKeysResponse = z.infer<
  typeof SearchOrganizationFileImportKeysResponseSchema
>;

/**
 * Builds the key that identifies an already-imported file, combining its
 * destination-relative path with the asset size and type. Shared so an upload
 * can compare local files against vault entries before sending any bytes.
 */
export function buildImportDedupKey(
  relativePath: string,
  fileSize: number | null,
  fileType: string | null,
): string {
  return [relativePath, fileSize ?? "", fileType ?? ""].join(" ");
}

const OrganizationFolderNameSchema = z
  .string()
  .trim()
  .min(1, "Enter a folder name")
  .refine(
    (name) =>
      name !== "." &&
      name !== ".." &&
      !name.includes("/") &&
      !name.includes("\\") &&
      !name.includes("\0"),
    'Folder name cannot be exactly "." or "..", or contain slashes',
  );

export const CreateOrganizationFolderParamsSchema = z.object({
  parentFolderId: z.string().nullable().optional(),
  name: OrganizationFolderNameSchema,
});
export type CreateOrganizationFolderParams = z.infer<
  typeof CreateOrganizationFolderParamsSchema
>;

export const ImportOrganizationFileZipParamsSchema = z.object({
  parentFolderId: z.string().nullable().optional(),
  assetId: z.string().min(1, "Asset ID is required"),
  deleteSourceAsset: z.boolean().optional(),
});
export type ImportOrganizationFileZipParams = z.infer<
  typeof ImportOrganizationFileZipParamsSchema
>;

export const DeleteOrganizationFilesParamsSchema = z.object({
  organizationFileIds: z
    .array(z.string())
    .min(1, "At least one file ID is required"),
});
export type DeleteOrganizationFilesParams = z.infer<
  typeof DeleteOrganizationFilesParamsSchema
>;

export const ListOrganizationFilesParamsSchema = z.object({
  search: z.string().optional(),
  parentFolderId: z.string().nullable().optional(),
  // When true, lists file entries only — folder rows are excluded and results
  // span all folders instead of scoping to parentFolderId.
  excludeFolders: z.boolean().optional(),
  limit: z.number().optional(),
  offset: z.number().optional(),
});
export type ListOrganizationFilesParams = z.infer<
  typeof ListOrganizationFilesParamsSchema
>;

export const ListOrganizationFilesResponseSchema = z.object({
  files: z.array(HydratedOrganizationFileSchema),
  currentFolder: HydratedOrganizationFileSchema.nullable(),
  breadcrumbs: z.array(OrganizationFileBreadcrumbSchema),
  total: z.number(),
});
export type ListOrganizationFilesResponse = z.infer<
  typeof ListOrganizationFilesResponseSchema
>;

export const AddOrganizationFilesResponseSchema = z.object({
  files: z.array(HydratedOrganizationFileSchema),
});
export type AddOrganizationFilesResponse = z.infer<
  typeof AddOrganizationFilesResponseSchema
>;

export const CreateOrganizationFolderResponseSchema = z.object({
  folder: HydratedOrganizationFileSchema,
});
export type CreateOrganizationFolderResponse = z.infer<
  typeof CreateOrganizationFolderResponseSchema
>;

export const ImportOrganizationFileZipResponseSchema = z.object({
  eventId: z.string(),
});
export type ImportOrganizationFileZipResponse = z.infer<
  typeof ImportOrganizationFileZipResponseSchema
>;

export const UpdateOrganizationFileParamsSchema = z
  .object({
    organizationFileId: z.string(),
    name: z.string().min(1, "Name is required").optional(),
    fileName: z.string().min(1, "File name is required").optional(),
    // Destination folder to move the entry into; null moves it to the root.
    parentFolderId: z.string().nullable().optional(),
  })
  .refine(
    (data) =>
      data.name !== undefined ||
      data.fileName !== undefined ||
      data.parentFolderId !== undefined,
    {
      message: "Name or destination folder is required",
    },
  );
export type UpdateOrganizationFileParams = z.infer<
  typeof UpdateOrganizationFileParamsSchema
>;

export const UpdateOrganizationFileResponseSchema = z.object({
  file: HydratedOrganizationFileSchema,
});
export type UpdateOrganizationFileResponse = z.infer<
  typeof UpdateOrganizationFileResponseSchema
>;
